import { useEffect, useState } from 'react';
import { ChatBubbleLeftRightIcon, ArrowPathIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import usePermisos from '../../hooks/usePermisos';
import NoPermissions from '../NoPermissions/NoPermissions';
import Loader from '../../Layout/Loaders/Loader';
import axios from '../../axios';
import notify from '../../Utils/notify';
import ConversationsList from './ConversationsList';
import ChatView from './ChatView';

const PERMISOS_REQUERIDOS = ['whatsapp_logs_read', 'whatsapp_reply'];

const WhatsAppChat = () => {
    const { loading: loadingPermisos, tienePermiso } = usePermisos(PERMISOS_REQUERIDOS);
    
    const [conversations, setConversations] = useState([]);
    const [selectedPhone, setSelectedPhone] = useState(null);
    const [messages, setMessages] = useState([]);
    const [patientName, setPatientName] = useState('');
    const [appointment, setAppointment] = useState(null);
    const [canReply, setCanReply] = useState(true);
    const [loadingConversations, setLoadingConversations] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [isTabActive, setIsTabActive] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Filtrado local por nombre de paciente, número o último mensaje
    const filteredConversations = conversations.filter((conversation) => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.trim().toLowerCase();
        return (
            conversation.patient_name?.toLowerCase().includes(term) ||
            conversation.phone_number?.toLowerCase().includes(term) ||
            conversation.last_message?.toLowerCase().includes(term)
        );
    });

    // Detectar si la pestaña está activa
    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsTabActive(!document.hidden);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    // Polling inteligente para conversaciones (lista)
    useEffect(() => {
        cargarConversaciones();
        
        // Intervalo adaptativo: 60s sin conversación, 30s con conversación (para actualizar lista)
        const intervalTime = selectedPhone ? 30000 : 60000;
        
        console.log(`🔄 Polling de conversaciones iniciado: cada ${intervalTime / 1000}s`);
        
        const interval = setInterval(() => {
            // Solo hacer polling si la pestaña está activa
            if (isTabActive) {
                console.log('🔄 Auto-refresh conversaciones (silent)');
                cargarConversaciones(true);
            } else {
                console.log('⏸️  Polling pausado - pestaña inactiva');
            }
        }, intervalTime);

        return () => {
            console.log('🛑 Polling de conversaciones detenido');
            clearInterval(interval);
        };
    }, [selectedPhone, isTabActive]);

    // Polling rápido para mensajes de conversación activa
    useEffect(() => {
        if (!selectedPhone || !isTabActive) return;

        console.log(`💬 Polling de mensajes iniciado para ${selectedPhone}: cada 8s`);

        // Refresh rápido cada 8 segundos para la conversación activa
        const interval = setInterval(() => {
            console.log('💬 Auto-refresh mensajes (silent)');
            cargarMensajes(selectedPhone, true);
        }, 8000);

        return () => {
            console.log('🛑 Polling de mensajes detenido');
            clearInterval(interval);
        };
    }, [selectedPhone, isTabActive]);

    // Cargar conversaciones
    const cargarConversaciones = async (silent = false) => {
        if (!silent) setLoadingConversations(true);
        
        try {
            const response = await axios.get('api/whatsapp/conversations');
            setConversations(response.data || []);
            setLastUpdate(new Date());
            
            if (!silent) {
                console.log('✅ Conversaciones cargadas:', response.data?.length || 0);
            }
        } catch (error) {
            console.error('❌ Error al cargar conversaciones:', error);
            if (!silent) {
                notify.error('Error al cargar conversaciones');
            }
        } finally {
            setLoadingConversations(false);
            setRefreshing(false);
        }
    };

    // Cargar mensajes de una conversación
    const cargarMensajes = async (phone, silent = false) => {
        if (!silent) setLoadingMessages(true);
        
        try {
            const response = await axios.get(`api/whatsapp/conversations/${phone}/messages`);
            setMessages(response.data.messages || []);
            setPatientName(response.data.patient_name || 'Desconocido');
            setAppointment(response.data.appointment || null);
            setCanReply(response.data.can_reply ?? true);
        } catch (error) {
            console.error('Error al cargar mensajes:', error);
            if (!silent) {
                notify.error('Error al cargar mensajes');
            }
        } finally {
            if (!silent) setLoadingMessages(false);
        }
    };

    // Seleccionar conversación
    const handleSelectConversation = (phone) => {
        setSelectedPhone(phone);
        cargarMensajes(phone);
    };

    // Enviar mensaje
    const handleSendMessage = async (phone, message) => {
        try {
            const response = await axios.post('api/whatsapp/reply', {
                to: phone,
                message: message,
            });

            notify.success('Mensaje enviado correctamente');

            // Recargar mensajes de la conversación
            await cargarMensajes(phone);
            
            // Recargar lista de conversaciones (para actualizar estado)
            await cargarConversaciones(true);

        } catch (error) {
            console.error('Error al enviar mensaje:', error);
            notify.error(error.response?.data?.message || 'Error al enviar mensaje');
            throw error;
        }
    };

    // Refresh manual
    const handleRefresh = () => {
        setRefreshing(true);
        cargarConversaciones();
        if (selectedPhone) {
            cargarMensajes(selectedPhone);
        }
    };

    if (loadingPermisos) {
        return <Loader />;
    }

    if (!tienePermiso('whatsapp_logs_read') || !tienePermiso('whatsapp_reply')) {
        return <NoPermissions />;
    }

    return (
        <div>

        <div className="container-admin-menu">
            <Breadcrumb hasButton={tienePermiso('users_create')} hasFilters={false} />
        </div>

        <div className="container-admin-container">
            <div className="container-admin bg-white rounded-lg shadow-md p-4">
                <div className="flex flex-col md:flex-row md:items-center info-badge rounded-xl p-4 shadow-sm mb-6 w-full">
                    <div className="flex flex-col md:flex-row md:items-center w-full">
                        <ChatBubbleLeftRightIcon className="h-6 w-6 text-[#25D366]" />
                        <h3>
                            WhatsApp Chat <br></br>
                            <p className="text-sm text-gray-500 mt-1">
                                Historial de conversaciones (42h) · Respuesta gratuita dentro de 24h
                                {!isTabActive && (
                                    <span className="ml-2 text-xs text-orange-500">• Pausado (pestaña inactiva)</span>
                                )}
                                {isTabActive && selectedPhone && (
                                    <span className="ml-2 text-xs text-green-600">• Actualización rápida (8s)</span>
                                )}
                                {isTabActive && !selectedPhone && (
                                    <span className="ml-2 text-xs text-white">• Actualización normal (60s)</span>
                                )}
                            </p>
                        </h3>
                        <img className="w-20" src="/images/whatsapp.png" alt="whatsapp" />
                        
                        <div className="flex items-center ml-auto">
                            {/* Refresh Button */}
                            <button
                                onClick={handleRefresh}
                                disabled={refreshing}
                                className="flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-lg transition-colors disabled:opacity-50"
                            >
                                <ArrowPathIcon className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
                                <span className="font-medium">Actualizar</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Content - 2 Column Layout */}
                <div className="flex-1 flex overflow-hidden bg-gray-100" style={{ height: '80vh',}}>
                    {/* Left Sidebar - Conversations List */}
                    <div className="w-full md:w-96 bg-white border-r border-gray-200 flex flex-col min-w-0 overflow-hidden" >
                        {/* Sidebar Header */}
                        <div className="bg-[#c1f0f8] border-b border-blue-500 w-full min-w-0">
                            <div className="font-semibold text-gray-700 text-sm m-0 p-0">
                                <span className="flex items-center m-0 p-0">
                                    <img className="w-26 h-15 object-contain" src="/images/logo2.png" alt="Dento Metric" />
                                    <p className="text-[18px] text-blue-700">Conversaciones <br></br> Activas </p>
                                </span>
                            </div>

                            {/* Buscador */}
                            <div className="relative m-0 p-0 w-full min-w-0 px-2">
                                <MagnifyingGlassIcon className="absolute left-4.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Buscar por nombre, número o mensaje..."
                                    className="w-full max-w-full box-border pl-8 pr-8 py-1.5 text-xs rounded-lg dento-input"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-end text-gray-400 hover:text-gray-600"
                                    >
                                        <XMarkIcon className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                            <div className="font-semibold text-gray-700 text-sm text-sm m-0 p-0">
                                <div className="flex items-center gap-2">
                                    <p>
                                        {lastUpdate && (
                                            <span className="ml-2 text-[12px] text-gray-400 py-4">
                                                • Actualizado: {lastUpdate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-xs text-[12px] text-gray-500 mt-0.5">
                                        {filteredConversations.length} de {conversations.length} conversaciones
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Conversations List */}
                        <div className="flex-1 overflow-hidden">
                            <ConversationsList
                                conversations={filteredConversations}
                                selectedPhone={selectedPhone}
                                onSelectConversation={handleSelectConversation}
                                loading={loadingConversations}
                            />
                        </div>
                    </div>

                    {/* Right Panel - Chat View */}
                    <div className="flex-1 flex flex-col">
                        <ChatView
                            phoneNumber={selectedPhone}
                            patientName={patientName}
                            appointment={appointment}
                            messages={messages}
                            onSendMessage={handleSendMessage}
                            loading={loadingMessages}
                            canReply={canReply}
                        />
                    </div>
                </div>
                
            </div>
        </div>
        </div>
    );
};

export default WhatsAppChat;
